import { ref } from 'vue';
import notify from '@/utils/app-notification';

export interface IPromiseQueue {
  enqueue: (fn: () => Promise<unknown>) => Promise<unknown>;
  enqueueNext: (fn: () => Promise<unknown>) => Promise<unknown>;
  clear: () => void;
  size: () => number;
}

export function usePromiseQueue(): IPromiseQueue {
  const queue = ref<(() => Promise<unknown>)[]>([]);
  let queueRunning = false;

  async function processQueue() {
    if (!queue.value.length || queueRunning) return;
    queueRunning = true;
    const fn = queue.value.shift();
    fn && (await fn());
    queueRunning = false;
    processQueue();
  }

  function enqueue(fn: () => Promise<unknown>) {
    let resolve: (value: unknown) => void;
    let reject: (reason?: unknown) => void;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });

    const task = () => {
      return fn()
        .then(resolve)
        .catch((error: unknown) => {
          notify({
            type: 'error',
            message: 'An operation failed. Please try again.',
          });
          reject(error);
        });
    };
    queue.value.push(task);
    processQueue();

    return promise;
  }

  function enqueueNext(fn: () => Promise<unknown>) {
    let resolve: (value?: unknown) => void;
    let reject: (reason?: unknown) => void;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });

    const task = () => {
      return fn()
        .then(resolve)
        .catch((error: unknown) => {
          notify({
            type: 'error',
            message: 'An operation failed. Please try again.',
          });
          reject(error);
        });
    };

    queue.value.unshift(task);
    processQueue();

    return promise;
  }

  function clear() {
    while (queue.value.length) queue.value.pop();
  }

  return {
    enqueue,
    enqueueNext,
    clear,
    size: () => queue.value.length,
  };
}
